import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, FileText, Loader2, Upload, X } from 'lucide-react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import SmrFormPanelV2 from './components/SmrFormPanelV2'
import { isSubstantiveSmrForm } from './components/smrFormPanelCore'

import { formatSydneyDate, formatSydneyDateTime } from '../../utils/sydneyDateTime'
const STEPS = [
  { id: 'patron', label: 'Patron' },
  { id: 'filing', label: 'Historical filing' },
  { id: 'smr', label: 'SMR form' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'review', label: 'Review' },
]

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export default function SmrArchivalRegister() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const caseIdFromUrl = searchParams.get('caseId') || ''
  const stepFromUrl = searchParams.get('step') || 'patron'

  const [step, setStep] = useState(stepFromUrl)
  const [patronQ, setPatronQ] = useState('')
  const [patrons, setPatrons] = useState([])
  const [selectedPatron, setSelectedPatron] = useState(null)
  const [caseId, setCaseId] = useState(caseIdFromUrl)
  const [caseNumber, setCaseNumber] = useState('')
  const [bundle, setBundle] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const [austracRef, setAustracRef] = useState('')
  const [filedAtLocal, setFiledAtLocal] = useState('')
  const [filingMethod, setFilingMethod] = useState('manual')
  const [filingNotes, setFilingNotes] = useState('')
  const [archivalReason, setArchivalReason] = useState('')
  const [confirmationImage, setConfirmationImage] = useState(null)
  const [attachments, setAttachments] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadingSmrPdf, setUploadingSmrPdf] = useState(false)
  const [smrDraft, setSmrDraft] = useState(null)
  const smrFormApiRef = useRef(null)

  const smrPdfAttachment = useMemo(
    () => attachments.find((a) => String(a?.attachment_type || '').toLowerCase() === 'smr') || null,
    [attachments]
  )

  const smrFormSubstantive = useMemo(
    () => isSubstantiveSmrForm(smrDraft) || Boolean(bundle?.form_substantive),
    [smrDraft, bundle?.form_substantive]
  )
  const smrSourceReady = Boolean(smrPdfAttachment) || smrFormSubstantive
  const supportingAttachments = useMemo(
    () => attachments.filter((a) => String(a?.attachment_type || '').toLowerCase() !== 'smr'),
    [attachments]
  )

  const goStep = (next) => {
    setStep(next)
    const p = new URLSearchParams(searchParams)
    p.set('step', next)
    if (caseId) p.set('caseId', caseId)
    setSearchParams(p, { replace: true })
  }

  const loadBundle = useCallback(async (id) => {
    if (!id) return
    const res = await authFetch(getLocalApiUrl(`/api/smr-registry/archival/${encodeURIComponent(id)}`))
    const j = await res.json()
    if (!res.ok || !j.success) throw new Error(j.error || 'Failed to load draft')
    setBundle(j.data)
    setCaseNumber(j.data?.case?.case_number || '')
    const meta = j.data?.meta || {}
    if (meta.austrac_reference) setAustracRef(meta.austrac_reference)
    if (meta.filed_at) setFiledAtLocal(meta.filed_at)
    if (meta.filing_method) setFilingMethod(meta.filing_method)
    if (meta.filing_notes) setFilingNotes(meta.filing_notes)
    if (meta.archival_reason) setArchivalReason(meta.archival_reason)
    setAttachments(j.data?.attachments || [])
    if (j.data?.registered) {
      toast.success('This historical SMR is already registered.')
      navigate('/compliance-safe/smr-registry')
    }
  }, [navigate])

  useEffect(() => {
    if (caseIdFromUrl) {
      setCaseId(caseIdFromUrl)
      loadBundle(caseIdFromUrl).catch((e) => setError(e.message))
    }
  }, [caseIdFromUrl, loadBundle])

  useEffect(() => {
    if (stepFromUrl !== step) setStep(stepFromUrl)
  }, [stepFromUrl])

  const searchPatrons = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await authFetch(
        getLocalApiUrl(`/api/smr-registry/archival/patrons?q=${encodeURIComponent(patronQ)}`)
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Search failed')
      setPatrons(j.data || [])
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const startDraft = async () => {
    if (!selectedPatron?.master_profile_id) {
      setError('Select a patron to continue.')
      return
    }
    setBusy(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl('/api/smr-registry/archival/draft'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ master_profile_id: selectedPatron.master_profile_id }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Could not start draft')
      const id = j.data.caseId
      setCaseId(id)
      setCaseNumber(j.data.caseNumber || '')
      const p = new URLSearchParams(searchParams)
      p.set('caseId', id)
      p.set('step', 'filing')
      setSearchParams(p, { replace: true })
      setStep('filing')
      await loadBundle(id)
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const saveMeta = async () => {
    if (!caseId) return false
    setBusy(true)
    setError('')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/smr-registry/archival/${encodeURIComponent(caseId)}/meta`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          austrac_reference: austracRef,
          filed_at: filedAtLocal,
          filing_method: filingMethod,
          filing_notes: filingNotes,
          archival_reason: archivalReason,
        }),
      })
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Failed to save filing details')
      return true
    } catch (e) {
      setError(e.message)
      return false
    } finally {
      setBusy(false)
    }
  }

  const onFilingNext = async () => {
    if (!austracRef.trim()) {
      setError('AUSTRAC reference is required.')
      return
    }
    if (!filedAtLocal) {
      setError('Lodged date is required.')
      return
    }
    if (!archivalReason.trim() || archivalReason.trim().length < 10) {
      setError('Archival reason is required (at least 10 characters).')
      return
    }
    const ok = await saveMeta()
    if (ok) goStep('smr')
  }

  const uploadSmrPdf = async (file) => {
    if (!caseId || !file) return
    setUploadingSmrPdf(true)
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await authFetch(
        getLocalApiUrl(`/api/smr-registry/archival/${encodeURIComponent(caseId)}/smr-pdf`),
        { method: 'POST', body: form }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'SMR PDF upload failed')
      if (j.data?.id != null) {
        setAttachments((prev) => [
          j.data,
          ...prev.filter((a) => String(a?.attachment_type || '').toLowerCase() !== 'smr'),
        ])
      } else {
        await loadBundle(caseId)
      }
      toast.success('SMR PDF uploaded')
    } catch (e) {
      setError(e.message)
    } finally {
      setUploadingSmrPdf(false)
    }
  }

  const uploadEvidence = async (file) => {
    if (!caseId || !file) return
    setUploading(true)
    setError('')
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await authFetch(
        getLocalApiUrl(`/api/smr-registry/archival/${encodeURIComponent(caseId)}/attachments`),
        { method: 'POST', body: form }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Upload failed')
      if (j.data?.id != null) {
        setAttachments((prev) => [j.data, ...prev])
      } else {
        const listRes = await authFetch(
          getLocalApiUrl(`/api/smr-registry/archival/${encodeURIComponent(caseId)}/attachments`)
        )
        const lj = await listRes.json()
        if (listRes.ok && lj.success) setAttachments(lj.data || [])
      }
      toast.success('Evidence uploaded')
    } catch (e) {
      setError(e.message)
    } finally {
      setUploading(false)
    }
  }

  const removeAttachment = async (attachmentId) => {
    if (!caseId) return
    try {
      const res = await authFetch(
        getLocalApiUrl(
          `/api/smr-registry/archival/${encodeURIComponent(caseId)}/attachments/${attachmentId}`
        ),
        { method: 'DELETE' }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Delete failed')
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId))
    } catch (e) {
      toast.error(e.message)
    }
  }

  const register = async () => {
    if (!caseId) return
    setBusy(true)
    setError('')
    try {
      const draft = smrFormApiRef.current?.draft ?? smrDraft
      if (smrFormApiRef.current?.persistDraft && draft) {
        await smrFormApiRef.current.persistDraft(draft, { silent: true })
      }
      if (!smrPdfAttachment && !isSubstantiveSmrForm(draft)) {
        throw new Error(
          'Complete the SMR form (narrative, offence type, indicators, or transactions) or upload the filed SMR PDF before registering.'
        )
      }

      let confirmationPayload = null
      if (confirmationImage?.file) {
        const dataUrl = await fileToDataUrl(confirmationImage.file)
        confirmationPayload = {
          data: dataUrl,
          mime_type: confirmationImage.file.type,
          filename: confirmationImage.name,
        }
      }
      const res = await authFetch(
        getLocalApiUrl(`/api/smr-registry/archival/${encodeURIComponent(caseId)}/register`),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            austrac_reference: austracRef,
            filed_at: filedAtLocal,
            filing_method: filingMethod,
            filing_notes: filingNotes,
            archival_reason: archivalReason,
            confirmation_image: confirmationPayload,
            smr_data: draft || undefined,
          }),
        }
      )
      const j = await res.json()
      if (!res.ok || !j.success) throw new Error(j.error || 'Registration failed')
      toast.success('Historical SMR registered in the registry.')
      navigate('/compliance-safe/smr-registry')
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  const patronName = bundle?.case?.member_name || selectedPatron?.label || ''
  const memberId = bundle?.case?.member_id || selectedPatron?.member_id || ''

  return (
    <div className="max-w-5xl mx-auto pb-16">
      <div className="mb-6">
        <button
          type="button"
          className="text-sm text-teal-700 flex items-center gap-1 mb-3"
          onClick={() => navigate('/compliance-safe/smr-registry')}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to SMR Registry
        </button>
        <h1 className="text-2xl font-semibold text-slate-900">Register historical SMR</h1>
        <p className="text-sm text-slate-600 mt-1">
          Archival entry — linked to a patron, not an investigation. Audit records when this was registered in PaySafeGO.
        </p>
        {caseNumber ? (
          <p className="text-xs text-slate-500 mt-1 font-mono">{caseNumber}</p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {STEPS.map((s) => (
          <button
            key={s.id}
            type="button"
            disabled={s.id !== 'patron' && !caseId}
            onClick={() => goStep(s.id)}
            className={`rounded-full px-3 py-1 text-xs font-semibold border ${
              step === s.id
                ? 'border-teal-600 bg-teal-50 text-teal-800'
                : 'border-slate-200 bg-white text-slate-600'
            } disabled:opacity-40`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      ) : null}

      {step === 'patron' && (
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">Select patron</h2>
          <p className="text-sm text-slate-600">Search by name, PAT id, membership number, or visitor id.</p>
          <div className="flex gap-2">
            <input
              className="input-field flex-1"
              placeholder="Search patrons…"
              value={patronQ}
              onChange={(e) => setPatronQ(e.target.value)}
            />
            <button type="button" className="btn-secondary" onClick={searchPatrons} disabled={busy}>
              {busy ? 'Searching…' : 'Search'}
            </button>
          </div>
          {selectedPatron ? (
            <div className="rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm text-teal-900">
              Selected:{' '}
              <span className="font-semibold">{selectedPatron.label}</span>
              {selectedPatron.master_profile_id ? (
                <span className="ml-2 font-mono text-xs text-teal-800">{selectedPatron.master_profile_id}</span>
              ) : null}
            </div>
          ) : null}
          <ul className="divide-y border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
            {patrons.map((p) => {
              const selected = selectedPatron?.value === p.value
              return (
              <li key={p.value}>
                <button
                  type="button"
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${
                    selected ? 'bg-teal-50 ring-2 ring-inset ring-teal-500' : ''
                  }`}
                  onClick={() => setSelectedPatron(p)}
                  aria-pressed={selected}
                >
                  <span className="font-medium">{p.label}</span>
                  <span className="text-slate-500 ml-2 font-mono text-xs">{p.master_profile_id}</span>
                  {selected ? <span className="ml-2 text-xs font-semibold text-teal-800">Selected</span> : null}
                </button>
              </li>
              )
            })}
            {patrons.length === 0 ? (
              <li className="px-3 py-4 text-sm text-slate-500">Search to find patrons.</li>
            ) : null}
          </ul>
          <div className="flex justify-end">
            <button type="button" className="btn-primary" disabled={busy || !selectedPatron} onClick={startDraft}>
              {busy ? 'Starting…' : 'Continue'}
            </button>
          </div>
        </div>
      )}

      {step === 'filing' && caseId && (
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">Historical AUSTRAC filing</h2>
          <p className="text-sm text-slate-600">When was this SMR lodged with AUSTRAC (not when you enter it here).</p>
          <label className="block text-sm font-medium text-slate-700">
            AUSTRAC reference
            <input className="input-field mt-1" value={austracRef} onChange={(e) => setAustracRef(e.target.value)} />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Lodged with AUSTRAC (date & time)
            <input
              type="datetime-local"
              className="input-field mt-1"
              value={filedAtLocal}
              onChange={(e) => setFiledAtLocal(e.target.value)}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Filing method
            <select className="select-field mt-1" value={filingMethod} onChange={(e) => setFilingMethod(e.target.value)}>
              <option value="manual">Manual / AUSTRAC Online</option>
              <option value="portal">Portal</option>
              <option value="system">System</option>
            </select>
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Filing notes (optional)
            <textarea className="input-field mt-1" rows={2} value={filingNotes} onChange={(e) => setFilingNotes(e.target.value)} />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            Archival reason (required)
            <textarea
              className="input-field mt-1"
              rows={3}
              placeholder="e.g. Pre-PaySafeGO paper register migration"
              value={archivalReason}
              onChange={(e) => setArchivalReason(e.target.value)}
            />
          </label>
          <label className="block text-sm font-medium text-slate-700">
            AUSTRAC confirmation screenshot (optional)
            <input
              type="file"
              accept="image/*"
              className="mt-1 text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (!file) return
                setConfirmationImage({
                  file,
                  preview: URL.createObjectURL(file),
                  name: file.name,
                })
              }}
            />
          </label>
          {confirmationImage?.preview ? (
            <img src={confirmationImage.preview} alt="" className="max-h-32 rounded border" />
          ) : null}
          <div className="flex justify-between">
            <button type="button" className="btn-secondary" onClick={() => goStep('patron')}>Back</button>
            <button type="button" className="btn-primary" disabled={busy} onClick={onFilingNext}>
              {busy ? 'Saving…' : 'Continue to SMR form'}
            </button>
          </div>
        </div>
      )}

      {step === 'smr' && caseId && (
        <div className="space-y-4">
          <div className="card space-y-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-teal-700 mt-0.5 shrink-0" />
              <div className="flex-1 space-y-3">
                <div>
                  <h2 className="text-lg font-semibold">Upload filed SMR (PDF)</h2>
                  <p className="text-sm text-slate-600 mt-1">
                    If you have the original SMR as filed with AUSTRAC, upload the PDF here. You can register
                    from the PDF alone without re-entering every field in the form below.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 btn-primary cursor-pointer">
                  {uploadingSmrPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {smrPdfAttachment ? 'Replace SMR PDF' : 'Upload SMR PDF'}
                  <input
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) uploadSmrPdf(f)
                      e.target.value = ''
                    }}
                  />
                </label>
                {smrPdfAttachment ? (
                  <div className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 text-sm">
                    <span className="font-medium text-teal-900">{smrPdfAttachment.file_name || 'SMR.pdf'}</span>
                    <button
                      type="button"
                      className="text-red-600"
                      onClick={() => removeAttachment(smrPdfAttachment.id)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500">Accepted format: PDF only.</p>
                )}
              </div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50">
              <h2 className="text-sm font-semibold text-slate-800">SMR form</h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Optional when a filed SMR PDF is uploaded above. Use this if you need to capture fields electronically.
              </p>
            </div>
            <SmrFormPanelV2
              caseId={caseId}
              caseNumber={caseNumber}
              patronName={patronName}
              memberId={memberId}
              archivalMode
              apiMode="archival"
              formApiRef={smrFormApiRef}
              onDraftChange={setSmrDraft}
            />
            <div className="px-4 py-3 border-t flex justify-between bg-white">
              <button type="button" className="btn-secondary" onClick={() => goStep('filing')}>Back</button>
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  if (smrFormApiRef.current?.persistDraft && smrFormApiRef.current.draft) {
                    await smrFormApiRef.current.persistDraft(smrFormApiRef.current.draft, { silent: true })
                  }
                  goStep('evidence')
                }}
              >
                Continue to evidence
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'evidence' && caseId && (
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">Supporting evidence</h2>
          <p className="text-sm text-slate-600">
            Upload additional supporting documents (portal confirmations, register scans, correspondence).
            The filed SMR PDF is uploaded on the SMR step.
          </p>
          <label className="inline-flex items-center gap-2 btn-secondary cursor-pointer">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Upload file
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) uploadEvidence(f)
                e.target.value = ''
              }}
            />
          </label>
          <ul className="divide-y border rounded-lg">
            {supportingAttachments.filter(Boolean).map((a) => (
              <li key={a.id} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{a.file_name || 'Uploaded file'}</span>
                <button type="button" className="text-red-600" onClick={() => removeAttachment(a.id)}>
                  <X className="w-4 h-4" />
                </button>
              </li>
            ))}
            {supportingAttachments.length === 0 ? (
              <li className="px-3 py-4 text-slate-500 text-sm">No supporting files yet — optional.</li>
            ) : null}
          </ul>
          <div className="flex justify-between">
            <button type="button" className="btn-secondary" onClick={() => goStep('smr')}>Back</button>
            <button type="button" className="btn-primary" onClick={() => goStep('review')}>Review</button>
          </div>
        </div>
      )}

      {step === 'review' && caseId && (
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold">Review &amp; register</h2>
          <dl className="text-sm space-y-2">
            <div><dt className="text-slate-500">Patron</dt><dd className="font-medium">{patronName}</dd></div>
            <div><dt className="text-slate-500">AUSTRAC reference</dt><dd className="font-mono">{austracRef}</dd></div>
            <div><dt className="text-slate-500">Lodged with AUSTRAC</dt><dd>{filedAtLocal ? formatSydneyDateTime(new Date(filedAtLocal)) : '—'}</dd></div>
            <div><dt className="text-slate-500">Archival reason</dt><dd>{archivalReason}</dd></div>
            <div>
              <dt className="text-slate-500">SMR source</dt>
              <dd>
                {smrPdfAttachment
                  ? `PDF — ${smrPdfAttachment.file_name}`
                  : smrFormSubstantive
                    ? 'Electronic form'
                    : 'Not provided yet'}
              </dd>
            </div>
            <div><dt className="text-slate-500">Supporting evidence</dt><dd>{supportingAttachments.length}</dd></div>
          </dl>
          {!smrSourceReady ? (
            <p className="text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              Add a filed SMR PDF or complete the electronic SMR form (at least one substantive field such as
              narrative, offence type, indicators, or transactions) before registering.
            </p>
          ) : null}
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Registering creates an immutable registry entry. The audit trail will show this was added on{' '}
            {formatSydneyDate(new Date())}, not on the historical lodged date.
          </p>
          <div className="flex justify-between">
            <button type="button" className="btn-secondary" onClick={() => goStep('evidence')}>Back</button>
            <button type="button" className="btn-primary" disabled={busy || !smrSourceReady} onClick={register}>
              {busy ? 'Registering…' : 'Register historical SMR'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
